import React, { useMemo } from "react";
import { useRecoilState } from "recoil";
import ScreenCard from "../ScreenCard";
import { newMissionState } from "../../atoms/core";
import { CreateMissionScreensContext } from "../../contexts/CreateMissionManager";
import { useI18n } from "../../hooks/i18n-hooks";
import { PayrollSourceType } from "../../__generated__/graphql";
import { BodyMediumText } from "../../components/FormattedText";
import Gap from "../../ui_atoms/Gap";
import { useBubbleStepOrigin } from "../../hooks/use-bubble-step-origin";
import { Bubble } from "../../components/Bubble";
import { useTheme } from "../../hooks/use-theme";
import ListItem from "../../ui_atoms/ListItem";

const CreateMissionTimeSheet = () => {
    const i18n = useI18n();
    const { gridSize } = useTheme();
    const [mission, setMission] = useRecoilState(newMissionState);
    const { validateScreen } = React.useContext(CreateMissionScreensContext);
    const setPayrollSource = React.useCallback(
        (payrollSource: PayrollSourceType) => {
            setMission((oldMission) => {
                return {
                    ...oldMission,
                    payrollSource,
                };
            });
        },
        [setMission],
    );

    const validate = React.useCallback(() => {
        void validateScreen("CreateMissionTimeSheet");
    }, [validateScreen]);

    const items = useMemo(() => {
        return [
            {
                title: i18n.t("screens.user.create_mission_time_sheet.payroll_source_lift_title"),
                description: i18n.t(
                    "screens.user.create_mission_time_sheet.payroll_source_lift_description",
                ),
                value: PayrollSourceType.Lift,
                selectable: true,
            },
            {
                title: i18n.t(
                    "screens.user.create_mission_time_sheet.payroll_source_manual_client_title",
                ),

                description: i18n.t(
                    "screens.user.create_mission_time_sheet.payroll_source_manual_client_description",
                ),
                value: PayrollSourceType.ManualClient,
                tags: [
                    i18n.t(
                        "screens.user.create_mission_time_sheet.payroll_source_manual_client_tag",
                    ),
                ],
                selectable: true,
            },
        ];
    }, [i18n]);

    const origin = useBubbleStepOrigin("CreateMissionTimeSheet");

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.translate("next"),
                    onPress: validate,
                    isValid: !!mission.payrollSource,
                },
            ]}
        >
            <Bubble
                title={i18n.t("screens.user.create_mission_time_sheet.title")}
                origin={origin}
                bubbleStyle={{ paddingBottom: 0 }}
            >
                <BodyMediumText>
                    {i18n.t("screens.user.create_mission_time_sheet.info")}
                </BodyMediumText>
                <Gap gap={1} />
                {items.map((item, index) => (
                    <ListItem
                        key={item.value}
                        {...item}
                        checked={mission.payrollSource === item.value}
                        onPress={() => {
                            setPayrollSource(item.value);
                        }}
                        style={{
                            marginHorizontal: -(gridSize / 2),
                            ...(index === items.length - 1
                                ? {
                                      borderBottomWidth: 0,
                                  }
                                : {}),
                        }}
                    />
                ))}
            </Bubble>
        </ScreenCard>
    );
};

export default CreateMissionTimeSheet;
